import React from "react";
import { Box, Stack, Switch, CircularProgress } from "@mui/material";
import { displayFont, tokens } from "../theme";
import { pressable } from "./pressableProps";

/**
 * The settings shape, from the module-screens book: "Every module has a
 * configuration page and they should all look like this one. A list of groups
 * on the left, one column of fields on the right, and a save bar that only
 * appears once something has changed."
 *
 * Three rules from that page drive most of what is here:
 *
 *   One column, 620px at most. The book's reason is worth keeping: "a settings
 *   form read across two columns is a settings form people get wrong." Ours
 *   were two-column grids, which is exactly the shape that makes someone set
 *   the field beside the one they meant.
 *
 *   A toggle carries the consequence underneath it, not a tooltip. So
 *   <SettingsToggle> takes a description and shows it, always, rather than
 *   hiding it behind an info icon nobody hovers.
 *
 *   A locked control stays visible at reduced opacity and says who can unlock
 *   it. Hiding a setting someone cannot change makes them think it does not
 *   exist; naming the holder tells them who to go and ask.
 */

// The column cap the book names. Everything measured against it.
export const FIELD_COLUMN = "38.75rem"; // 620px

/** Left rail: the groups. */
export const SettingsRail = ({ groups, active, onSelect }) => (
  <Box
    component="nav"
    aria-label="Settings sections"
    sx={{ width: "13.75rem", flex: "none", display: "flex", flexDirection: "column", gap: "2px" }}
  >
    {groups.map((g) => {
      const selected = g.key === active;
      return (
        <Box
          key={g.key}
          {...pressable(() => onSelect(g.key), g.label)}
          aria-current={selected ? "true" : undefined}
          sx={{
            padding: "9px 12px",
            borderRadius: "8px",
            fontSize: "0.875rem",
            cursor: "pointer",
            // The selected group is weight and ground, not colour: a plum fill
            // here would compete with the primary action in the column beside it.
            fontWeight: selected ? 600 : 400,
            color: selected ? tokens.text : tokens.textMuted,
            background: selected ? tokens.surface : "transparent",
            "&:hover": { background: selected ? tokens.surface : tokens.surfaceSubtle },
          }}
        >
          {g.label}
        </Box>
      );
    })}
  </Box>
);

/** Rail on the left, one column of fields on the right. */
export const SettingsLayout = ({ groups, active, onSelect, children }) => (
  <Box sx={{ display: "flex", gap: "32px", alignItems: "flex-start", flexWrap: { xs: "wrap", md: "nowrap" } }}>
    {groups?.length > 0 && <SettingsRail groups={groups} active={active} onSelect={onSelect} />}
    <Box sx={{ flex: 1, maxWidth: FIELD_COLUMN, minWidth: 0, width: "100%" }}>{children}</Box>
  </Box>
);

/** The page's own title and the line of context under it. */
export const SettingsHeader = ({ title, subtitle }) => (
  <Box sx={{ mb: "26px" }}>
    <Box sx={{ fontFamily: displayFont, fontSize: "1.5rem", fontWeight: 700, mb: "4px", color: tokens.text }}>
      {title}
    </Box>
    {subtitle && <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>{subtitle}</Box>}
  </Box>
);

/** One group of settings within the column. */
export const SettingsSection = ({ title, description, children }) => (
  <Box sx={{ mb: "32px" }}>
    {title && (
      <Box sx={{ fontFamily: displayFont, fontSize: "1.1875rem", fontWeight: 700, mb: "6px", color: tokens.text }}>
        {title}
      </Box>
    )}
    {description && (
      <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, lineHeight: 1.6, mb: "28px" }}>{description}</Box>
    )}
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>{children}</Box>
  </Box>
);

/** A labelled field, with its consequence underneath rather than beside it. */
export const SettingsField = ({ label, help, htmlFor, children }) => (
  <Box>
    {label && (
      <Box
        component={htmlFor ? "label" : "div"}
        htmlFor={htmlFor}
        sx={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, mb: "6px", color: tokens.text }}
      >
        {label}
      </Box>
    )}
    {children}
    {help && <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "6px", lineHeight: 1.5 }}>{help}</Box>}
  </Box>
);

/** A hairline between a field group and the toggles that follow it. */
export const SettingsDivider = () => (
  <Box aria-hidden="true" sx={{ height: "1px", background: tokens.border }} />
);

/**
 * A toggle and the thing it does.
 *
 * `lockedBy` names the person or policy that holds it. The control stays on
 * screen at reduced opacity rather than disappearing, so the setting is known
 * to exist and the reader knows who to ask.
 */
export const SettingsToggle = ({ title, description, checked, onChange, lockedBy }) => {
  const locked = Boolean(lockedBy);
  return (
    <Stack direction="row" alignItems="flex-start" sx={{ gap: "16px", opacity: locked ? 0.55 : 1 }}>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ fontSize: "0.875rem", fontWeight: 600, mb: "3px", color: tokens.text }}>{title}</Box>
        {(description || locked) && (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.5 }}>
            {locked ? `Locked by your ${lockedBy}.` : description}
          </Box>
        )}
      </Box>
      <Switch
        checked={Boolean(checked)}
        onChange={onChange}
        disabled={locked}
        inputProps={{ "aria-label": title }}
      />
    </Stack>
  );
};

// The book writes the count as a word — "Two changes not yet saved". Past ten
// a numeral reads faster than a word does.
const WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
export const changeCountLabel = (n) =>
  `${n < WORDS.length ? WORDS[n] : n} ${n === 1 ? "change" : "changes"} not yet saved`;

/**
 * The save bar, which exists only when there is something to save.
 *
 * A permanently visible Save button gives no signal — it looks the same whether
 * the form is untouched or has three edits pending, so the only way to know is
 * to press it. Appearing on the first edit is itself the feedback.
 */
export const SettingsSaveBar = ({ dirtyCount = 0, onDiscard, onSave, saving, label = "Save changes", message }) => {
  if (!dirtyCount) return null;
  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        mt: "30px",
        padding: "16px 20px",
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: "10px",
      }}
    >
      <Box sx={{ flex: 1, fontSize: "0.8125rem", color: tokens.textMuted }}>
        {message || changeCountLabel(dirtyCount)}
      </Box>
      {onDiscard && (
        <Box
          {...pressable(onDiscard, "Discard changes")}
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: tokens.primary,
            cursor: "pointer",
            padding: "4px",
          }}
        >
          Discard
        </Box>
      )}
      <Box
        component="button"
        type="button"
        onClick={onSave}
        disabled={saving}
        sx={{
          background: tokens.primary,
          color: tokens.card,
          border: "none",
          borderRadius: "8px",
          padding: "10px 20px",
          fontSize: "0.8125rem",
          fontWeight: 600,
          fontFamily: "inherit",
          cursor: saving ? "default" : "pointer",
          "&:hover": { background: tokens.primaryHover },
          "&:disabled": { background: tokens.border, color: tokens.textDisabled },
        }}
      >
        {saving ? <CircularProgress size={"1rem"} sx={{ color: "inherit" }} /> : label}
      </Box>
    </Box>
  );
};

/**
 * Tracks a settings form against what was loaded, so the save bar knows both
 * whether to appear and how many fields are behind it.
 *
 * `reset` is called with what the server now holds after a successful save —
 * not with the local form — so a field the server normalised (an empty string
 * becoming 0) does not leave the bar stuck open on a change nobody made.
 */
export const useSettingsForm = (initial = {}) => {
  const [baseline, setBaseline] = React.useState(initial);
  const [form, setForm] = React.useState(initial);

  const load = React.useCallback((values) => {
    setBaseline(values);
    setForm(values);
  }, []);

  const set = React.useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  }, []);

  const changed = React.useMemo(
    () => Object.keys(form).filter((k) => String(form[k]) !== String(baseline[k])),
    [form, baseline]
  );

  const discard = React.useCallback(() => setForm(baseline), [baseline]);

  return { form, set, load, discard, dirtyCount: changed.length, changed };
};
